import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '#pplx/frontend/aether/components/Button/index.js';
import { Tooltip } from '#pplx/frontend/aether/components/Tooltip/index.js';
import { useConnectorModalContext } from '#pplx/frontend/apps/ask/connectors-list/ConnectorModalContextShared.js';
import { useCatalogSources } from '#pplx/frontend/apps/ask/connectors-list/hooks/useConnectors.js';
import { AddAppsAndToolsMenu } from '#pplx/frontend/apps/ask/connectors/components/AddAppsAndToolsMenu.js';
import { ConnectorAvatar } from '#pplx/frontend/apps/ask/connectors/components/ConnectorAvatar.js';
import { usePluginEntitiesEnabled } from '#pplx/frontend/apps/ask/connectors/hooks/usePluginsNavigationHrefs.js';
import { useOpenConnectorModalBySourceId } from '#pplx/frontend/apps/ask/connectors/modal-navigation/useOpenConnectorModalBySourceId.js';
import { isUserInstalledSource, resolveSourceAvatarUrl, } from '#pplx/frontend/apps/ask/connectors/utils/connectors.js';
import { homepageComputerConnectorLauncherClickedEvent } from '#pplx/frontend/apps/ask/events/comet-home/analytics.js';
import { useBrandedVisitorGate } from '#pplx/frontend/apps/ask/hooks/branded-visitor-gate-hooks/useBrandedVisitorGate.js';
import { useGlobalAvailableSources } from '#pplx/frontend/apps/ask/hooks/shared-source-selection-hooks/useSharedSourceSelection.js';
import { openSettings } from '#pplx/frontend/apps/ask/settings/settingsHash.js';
import { createStackedVisuals } from '#pplx/frontend/apps/ask/shared-ui-components/createStackedVisuals.js';
import { useSkillModal } from '#pplx/frontend/apps/ask/skills/hooks/useSkillModal.js';
import { useApiSources } from '#pplx/frontend/apps/ask/sources/hooks/useApiSources.js';
import { useSourceActivity } from '#pplx/frontend/apps/ask/user-settings/hooks/useSourceActivity.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { ModalOrigin } from '#pplx/frontend/components/types/types.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useRouter } from '#pplx/frontend/router/provider.js';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
const ConnectorsEnableModal = Dynamic(async () => {
    const { ConnectorsEnableModal } = await import('#pplx/frontend/apps/ask/connectors/modals/ConnectorsEnableModal.js');
    return { default: ConnectorsEnableModal };
}, { loading: () => null });
// Fetch a small buffer beyond the menu's eight-row cap so filtering can still fill eager suggestions.
const HOME_POPULAR_CONNECTOR_MENU_LIMIT = 10;
const HOME_CONNECTOR_VISUAL_LIMIT = 3;
const HOME_CONNECTOR_LOADING_VISUALS = Array.from({ length: HOME_CONNECTOR_VISUAL_LIMIT }, (_, index) => _jsx("span", { "aria-hidden": true, className: "size-3.5 squircle bg-subtle" }, index));
// Anonymous /rest/sources is access-filtered; discovery uses the public browsing catalog.
function useHomeConnectorDiscoverySources(params) {
    const { status } = useSession();
    const api = useApiSources({
        ...params,
        enabled: status === 'authenticated' && (params.enabled ?? true),
        filterBy: 'disconnected',
        catalogMode: true,
        excludeIneligible: true,
        groupByFamily: true,
        productSurface: 'computer',
    });
    const catalog = useCatalogSources({
        ...params,
        enabled: status === 'unauthenticated' && (params.enabled ?? true),
        groupByFamily: true,
        productSurface: 'computer',
    });
    return status === 'authenticated'
        ? {
            sources: api.sources,
            isSourcesLoading: api.isSourcesLoading,
            isSourcesError: api.isSourcesError,
        }
        : {
            sources: catalog.sources,
            isSourcesLoading: status === 'loading' || catalog.isLoading,
            isSourcesError: catalog.isError,
        };
}
const LegacyHomeComputerConnectorLauncher = React.memo(function HomeComputerConnectorLauncher({ onFocusInput, onInsertMentionNode, onRemoveMentionNode, }) {
    const { $t } = useIntl();
    const { enabled: pluginsEnabled } = usePluginEntitiesEnabled();
    const { status: sessionStatus } = useSession();
    const { openVisitorLoginUpsell } = useBrandedVisitorGate({
        enabled: sessionStatus === 'unauthenticated',
    });
    const { track } = useInstrumentation();
    const { openConnectorModal } = useConnectorModalContext();
    const openConnectorModalBySourceId = useOpenConnectorModalBySourceId({
        reason: 'skill-modal-connector',
    });
    const openSkillModal = useSkillModal({
        openConnectorModal: openConnectorModalBySourceId,
    });
    const router = useRouter();
    const { activity, trackSourceActivity } = useSourceActivity();
    const { sources: selectedSourceIds, setSources: setSelectedSourceIds } = useGlobalAvailableSources();
    const [hasOpenedMenu, setHasOpenedMenu] = useState(false);
    const { sources: connectedApiSources, isSourcesError: areConnectedSourcesError, isSourcesLoading: areConnectedSourcesLoading, } = useApiSources({
        reason: 'home-computer-connector-launcher-installed',
        filterBy: 'connected',
        noLimit: true,
        excludeIneligible: true,
        groupByFamily: true,
        productSurface: 'computer',
    });
    const { sources: popularApiSources, isSourcesError: arePopularSourcesError, isSourcesLoading: arePopularSourcesLoading, } = useHomeConnectorDiscoverySources({
        reason: 'home-computer-connector-launcher-popular',
        limit: HOME_POPULAR_CONNECTOR_MENU_LIMIT,
        popular: true,
    });
    const { sources: availableApiSources, isSourcesError: areAvailableSourcesError, isSourcesLoading: areAvailableSourcesLoading, } = useHomeConnectorDiscoverySources({
        reason: 'home-computer-connector-launcher-available',
        enabled: hasOpenedMenu,
        noLimit: true,
    });
    const connectedSources = useMemo(() => {
        if (areConnectedSourcesLoading || areConnectedSourcesError)
            return EMPTY_ARRAY;
        const recentRankBySourceId = new Map([...activity]
            .sort((left, right) => right.lastUsedAt - left.lastUsedAt)
            .map((item, index) => [item.sourceType, index]));
        return connectedApiSources
            .filter(isUserInstalledSource)
            .map((source, index) => ({ source, index }))
            .sort((left, right) => {
            const leftRank = recentRankBySourceId.get(left.source.id);
            const rightRank = recentRankBySourceId.get(right.source.id);
            if (leftRank !== undefined || rightRank !== undefined) {
                return (leftRank ?? Number.MAX_SAFE_INTEGER) - (rightRank ?? Number.MAX_SAFE_INTEGER);
            }
            return left.index - right.index;
        })
            .map(({ source }) => source);
    }, [activity, areConnectedSourcesError, areConnectedSourcesLoading, connectedApiSources]);
    const popularSources = useMemo(() => {
        if (arePopularSourcesLoading || arePopularSourcesError)
            return EMPTY_ARRAY;
        return popularApiSources.filter(isUserInstalledSource);
    }, [arePopularSourcesError, arePopularSourcesLoading, popularApiSources]);
    const availableSources = useMemo(() => {
        if (!hasOpenedMenu || areAvailableSourcesLoading || areAvailableSourcesError) {
            return EMPTY_ARRAY;
        }
        return availableApiSources.filter(isUserInstalledSource);
    }, [areAvailableSourcesError, areAvailableSourcesLoading, availableApiSources, hasOpenedMenu]);
    const areConnectorSourcesLoading = areConnectedSourcesLoading || arePopularSourcesLoading;
    const popularVisualSources = useMemo(() => popularSources
        .filter((source) => !connectedSources.some((connectedSource) => connectedSource.id === source.id))
        .filter((source) => resolveSourceAvatarUrl(source) != null)
        .slice(0, HOME_CONNECTOR_VISUAL_LIMIT), [connectedSources, popularSources]);
    const [initialVisualSources, setInitialVisualSources] = useState(null);
    useEffect(() => {
        if (areConnectorSourcesLoading || initialVisualSources !== null)
            return;
        setInitialVisualSources(popularVisualSources);
    }, [areConnectorSourcesLoading, initialVisualSources, popularVisualSources]);
    const visualSources = initialVisualSources ?? popularVisualSources;
    const triggerLabel = pluginsEnabled
        ? $t({
            id: '3f6fc45763',
            defaultMessage: 'Use a plugin',
            description: 'Homepage Computer input footer button when plugins are enabled',
        })
        : $t({
            id: 'f7ca52e485',
            defaultMessage: 'Connect an app',
            description: 'Homepage Computer input footer button for connecting apps and tools',
        });
    const triggerTooltip = $t({
        id: 'cfed64817e',
        defaultMessage: 'Connect your favorite apps and tools so that Computer can act on your data',
        description: 'Tooltip shown below the homepage Computer input button for connecting apps and tools',
    });
    const triggerVisual = createStackedVisuals(areConnectorSourcesLoading
        ? HOME_CONNECTOR_LOADING_VISUALS
        : visualSources.map((source) => (_jsx(ConnectorAvatar, { connectorAvatar: resolveSourceAvatarUrl(source), name: source.display_name ?? source.id, size: "tiny" }, source.id))), {
        isOverlapping: true,
        maxVisuals: HOME_CONNECTOR_VISUAL_LIMIT,
        size: 'xs',
        shape: 'squircle',
        stackDirection: 'right',
        showRemainderCount: false,
        tilt: true,
        containerClass: 'flex items-center',
    });
    const trackAction = useCallback((data) => {
        track(homepageComputerConnectorLauncherClickedEvent({
            name: 'homepage computer connector launcher clicked',
            data,
        }));
    }, [track]);
    const handleInstalledSourceSelect = useCallback((source) => {
        if (selectedSourceIds.includes(source.id)) {
            setSelectedSourceIds(selectedSourceIds.filter((sourceId) => sourceId !== source.id));
            onRemoveMentionNode({ uuid: source.id, variant: 'sources' });
            window.requestAnimationFrame(onFocusInput);
            trackAction({ action: 'deselect_installed', source: source.id });
            return;
        }
        trackSourceActivity(source.id);
        setSelectedSourceIds([...selectedSourceIds, source.id]);
        onInsertMentionNode({
            uuid: source.id,
            variant: 'sources',
            text: source.display_name ?? source.id,
            icon: { type: 'source', sourceId: source.id },
        }, { selectAfterInsert: false });
        window.requestAnimationFrame(onFocusInput);
        trackAction({ action: 'select_installed', source: source.id });
    }, [
        onInsertMentionNode,
        onRemoveMentionNode,
        onFocusInput,
        selectedSourceIds,
        setSelectedSourceIds,
        trackAction,
        trackSourceActivity,
    ]);
    const handleConnectPopularSource = useCallback((source) => {
        if (sessionStatus === 'loading')
            return;
        trackAction({ action: 'connect_popular', source: source.id });
        if (sessionStatus === 'unauthenticated') {
            openVisitorLoginUpsell({
                title: $t({
                    defaultMessage: 'Sign in to connect your apps',
                    description: 'Login modal title shown when a visitor tries to add a connector',
                    id: '648d617cc0',
                }),
                description: $t({
                    defaultMessage: 'Connect Gmail, Drive, GitHub, Notion, and more to search and execute tasks.',
                    description: 'Login modal description shown when a visitor tries to add a connector',
                    id: '90eab5d5ef',
                }),
                origin: ModalOrigin.CONNECTORS_PAGE,
            });
            return;
        }
        openConnectorModal(source.id, ConnectorsEnableModal, {
            connectorName: source.id,
            connectorAvatar: resolveSourceAvatarUrl(source),
            connectionType: (source.connection_type ??
                null),
            reason: 'home-computer-connector-launcher',
            referrer: 'computer',
            openInNewTab: true,
            showFeatures: false,
            source,
            openSkillModal,
        });
    }, [$t, openConnectorModal, openSkillModal, openVisitorLoginUpsell, sessionStatus, trackAction]);
    const handleManageApi = useCallback(() => {
        trackAction({ action: 'manage_api' });
        openSettings(router, '/credentials-vault');
    }, [router, trackAction]);
    return (_jsx(AddAppsAndToolsMenu, { analyticsSurface: "home_computer_input", side: "bottom", align: "start", triggerElement: _jsx(Tooltip, { content: triggerTooltip, side: "bottom", children: _jsx(Button, { variant: "text", size: "tiny", pill: true, "aria-label": triggerLabel, children: _jsxs("span", { className: "flex items-center gap-1", children: [_jsx("span", { className: "-ms-0.5 inline-flex shrink-0", children: triggerVisual }), _jsx("span", { className: "[@container_composer-chin_(width<480px)]:hidden", children: triggerLabel })] }) }) }), installedSources: connectedSources, isLoading: areConnectorSourcesLoading, isAvailableSourcesError: hasOpenedMenu && areAvailableSourcesError, isAvailableSourcesLoading: !hasOpenedMenu || areAvailableSourcesLoading, availableSources: availableSources, popularSources: popularSources, selectedSourceIds: selectedSourceIds, onOpen: () => {
            setHasOpenedMenu(true);
            trackAction({ action: 'open' });
        }, onInstalledSourceSelect: handleInstalledSourceSelect, onConnectPopularSource: handleConnectPopularSource, onViewMore: () => trackAction({ action: 'view_more' }), onManageApi: handleManageApi }));
});
const HomePluginLauncher = Dynamic(async () => {
    const { HomePluginLauncher } = await import('#pplx/frontend/apps/ask/computer-plugins/components/HomePluginLauncher.js');
    return { default: HomePluginLauncher };
}, { loading: () => null });
export const HomeComputerConnectorLauncher = React.memo(function HomeComputerConnectorLauncher(props) {
    const { enabled: pluginsEnabled } = usePluginEntitiesEnabled();
    if (pluginsEnabled) {
        return _jsx(HomePluginLauncher, { ...props });
    }
    return _jsx(LegacyHomeComputerConnectorLauncher, { ...props });
});
//# sourceMappingURL=HomeComputerConnectorLauncher.js.map